import { createApi } from '@reduxjs/toolkit/query/react';

import { CaseContext } from '../types/case-context-types';
import { baseQueryWithErrorHandling } from './baseQuery';
import { matterContextApi } from './matterContextService';

export const caseContextApprovalApi = createApi({
  reducerPath: 'caseContextApprovalApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['MatterContext'],
  endpoints: (builder) => ({
    approveCaseContextField: builder.mutation<
      CaseContext,
      { matterId: string; fieldName: string }
    >({
      query: ({ matterId, fieldName }) => ({
        url: `/api/matters/${matterId}/case-context/approve/${fieldName}/`,
        method: 'POST',
      }),
      // Don't invalidate, just update the cache directly
      async onQueryStarted(
        { matterId, fieldName },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data: updatedContext } = await queryFulfilled;
          // Update the matterContextApi cache with the new data
          dispatch(
            matterContextApi.util.updateQueryData(
              'getMatterContext',
              { matterId },
              (draft) => {
                Object.assign(draft, updatedContext);
              },
            ),
          );
        } catch {
          // If the mutation fails, we don't update the cache
        }
      },
    }),

    unapproveCaseContextField: builder.mutation<
      CaseContext,
      { matterId: string; fieldName: string }
    >({
      query: ({ matterId, fieldName }) => ({
        url: `/api/matters/${matterId}/case-context/unapprove/${fieldName}/`,
        method: 'POST',
      }),
      async onQueryStarted(
        { matterId, fieldName },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data: updatedContext } = await queryFulfilled;
          dispatch(
            matterContextApi.util.updateQueryData(
              'getMatterContext',
              { matterId },
              (draft) => {
                Object.assign(draft, updatedContext);
              },
            ),
          );
        } catch {
          // If the mutation fails, we don't update the cache
        }
      },
    }),

    bulkApproveCaseContext: builder.mutation<CaseContext, { matterId: string }>(
      {
        query: ({ matterId }) => ({
          url: `/api/matters/${matterId}/case-context/approve-all/`,
          method: 'POST',
        }),
        async onQueryStarted({ matterId }, { dispatch, queryFulfilled }) {
          try {
            const { data: updatedContext } = await queryFulfilled;
            dispatch(
              matterContextApi.util.updateQueryData(
                'getMatterContext',
                { matterId },
                (draft) => {
                  Object.assign(draft, updatedContext);
                },
              ),
            );
          } catch {
            // If the mutation fails, we don't update the cache
          }
        },
      },
    ),

    bulkUnapproveCaseContext: builder.mutation<
      CaseContext,
      { matterId: string }
    >({
      query: ({ matterId }) => ({
        url: `/api/matters/${matterId}/case-context/unapprove-all/`,
        method: 'POST',
      }),
      async onQueryStarted({ matterId }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedContext } = await queryFulfilled;
          dispatch(
            matterContextApi.util.updateQueryData(
              'getMatterContext',
              { matterId },
              (draft) => {
                Object.assign(draft, updatedContext);
              },
            ),
          );
        } catch {
          // If the mutation fails, we don't update the cache
        }
      },
    }),

    getCaseContextWithApprovals: builder.query<CaseContext, string>({
      query: (matterId) => `/api/matters/${matterId}/case-context/`,
      providesTags: (_result, _error, matterId) => [
        { type: 'MatterContext', id: matterId },
      ],
    }),
  }),
});

export const {
  useApproveCaseContextFieldMutation,
  useUnapproveCaseContextFieldMutation,
  useBulkApproveCaseContextMutation,
  useBulkUnapproveCaseContextMutation,
  useGetCaseContextWithApprovalsQuery,
} = caseContextApprovalApi;
